// src/util/cocktailCalculator.js

/**
 * Given an array of selected cocktails (with id, quantity),
 * compute total cost = sum(ingredient bottles × bottlePrice)
 *   + (costPerCocktail × totalCocktails).
 * Also returns bottle counts for the delivery API.
 *
 * @param {Array<{id: string, quantity: number}>} selectedCocktails
 * @param {Object} listingPublicData – listing.attributes.publicData
 * @param {function(string): Promise<number>} fetchBottlePrice  
 *        – async fn that, given an ingredient ID, returns its bottle price in subunits
 * @param {number} costPerCocktail  – host-facing “cost per cocktail” fee
 * @returns {Promise<{
 *   totalCost: number,
 *   breakdown: Array<{ingredientId: string, bottles: number}>,
 *   costPerCocktail: number,
 *   totalCocktails: number
 * }>}
 */
export async function calculateCocktailBundleCost(
  selectedCocktails,
  listingPublicData,
  fetchBottlePrice,
  costPerCocktail
) {
  let ingredientTotals = {}; // { ingredientId: bottleCountNeeded }
  let totalCocktails = 0;

  // 1️⃣ Aggregate max bottles needed per ingredient across all cocktails
  for (const { id: cocktailId, quantity } of selectedCocktails) {
    const cocktail = listingPublicData.cocktails.find(c => c.id === cocktailId);
    if (!cocktail) continue;

    totalCocktails += quantity;
    cocktail.ingredients.forEach(({ id: ingId, amountNeededPerCocktail }) => {
      const neededUnits = amountNeededPerCocktail * quantity;
      const bottles = Math.ceil(neededUnits / cocktail.bottleSize[ingId]);
      ingredientTotals[ingId] = Math.max(ingredientTotals[ingId] || 0, bottles);
    });
  }

  // 2️⃣ Fetch each bottle’s price and sum ingredient cost
  let ingredientCost = 0;
  const breakdown = [];
  for (const [ingId, bottleCount] of Object.entries(ingredientTotals)) {
    const bottlePrice = await fetchBottlePrice(ingId);
    ingredientCost += bottlePrice * bottleCount;
    breakdown.push({ ingredientId: ingId, bottles: bottleCount });
  }

  // 3️⃣ Add the host-facing “cost per cocktail” fee
  const costPerCocktailTotal = costPerCocktail * totalCocktails;
  const totalCost = ingredientCost + costPerCocktailTotal;

  return {
    totalCost,
    breakdown,
    costPerCocktail,
    totalCocktails,
  };
}
